import { WIX_AI_CHAT_ASSISTANT, WIX_CHAT } from '@wix/app-definition-ids';
import {
  DocumentServicesObject,
  EditorClientSpecMapEntry,
} from '@wix/document-services-types';
import { InstallInitiator } from '@wix/editor-platform-sdk-types';
import type {
  AddAppData,
  AppInstallOption,
  IntentInstallOptions,
  IntentInstallReturnValue,
  PlatformApiMethods,
} from '@wix/editor-platform-host-integration-apis';
import { uniq } from 'lodash';
import { PlatformContext } from '../../../types/platformApi';
import { UnifiedComponentsCollection } from '../../../types/unifiedComponents';
import {
  getEditorInfo,
  getIsImpersonated,
  shouldForceAppInstallation,
} from '../../../utils';
import { appMarketService } from '../../utils/appMarketService';
import { getAppType, isTpaWidgetApp } from '../../utils/appMarketUtils';
import { IExperimentsApiAdapter } from '../../utils/experimentsApiAdapter';
import { getPendingApps } from '../../utils/pendingAppsService';
import { ApplicationsInstaller } from './ApplicationsInstaller/ApplicationsInstaller';
import { SerialInstallationStrategyV2 } from './ApplicationsInstaller/SerialInstallationStrategyV2';
import { BIManager, IntentInstallBIEventStep } from './BIManager';
import { IntentApp, IntentInstallStore } from './IntentInstallStore';
import { PanelsManager } from './PanelsManager';
import {
  type ProgressBarManager,
  createProgressBarManager,
} from './ProgressBarManager';
import {
  PRE_INTENT_INSTALL_MODAL_EXPERIMENT,
  WIDGET_APPS_CONFIG_EXPERIMENT,
} from './constants';
import { IntentInstallErrorCode, createIntentInstallError } from './errors';
import {
  getIntentAppInfoFromQueryParams,
  getIntentInstallTestParams,
} from './helpers';
import {
  COMPANION_APP_DEPENDENCIES_INSTALL_SPEC,
  getMissingAppDependenciesInstallData,
} from '../appDependenciesInstallData';

type GetBiExtrasOptions = {
  appDefinitionId?: string;
  origin?: string;
};

// currently in intent install we automatically install only these widget apps
const allowedWidgetApps = new Set([WIX_CHAT, WIX_AI_CHAT_ASSISTANT]);
type AppDependenciesInstallData = {
  appDefinitionIds: string[];
  appsOptions: Record<string, AppInstallOption>;
};

const canInstallApp =
  (options: IntentInstallOptions) => (appData: EditorClientSpecMapEntry) => {
    if (options.shouldInstallWidgets) {
      return true;
    }

    return (
      allowedWidgetApps.has(appData.appDefinitionId) || !isTpaWidgetApp(appData)
    );
  };

const getIntentAppFromQueryParams = (
  ignoredApps: string[],
): IntentApp | undefined => {
  const intentApp = getIntentAppInfoFromQueryParams();

  const intentAppDefId = intentApp?.appDefId;

  if (!intentAppDefId || ignoredApps.includes(intentAppDefId)) {
    return;
  }

  return { appDefId: intentAppDefId, version: intentApp.version };
};

const isAppInitiator = (options: IntentInstallOptions) => {
  if (options.installOrigin) {
    return options.installOrigin.initiator === InstallInitiator.App;
  }
  if (options.appsOptions) {
    return Object.values(options.appsOptions).some(
      (appOption) => appOption.origin?.initiator === InstallInitiator.App,
    );
  }
  return false;
};

const defaultIntentInstallReturn: IntentInstallReturnValue = {
  failedApps: [],
  installedApps: {},
};

export class IntentInstallServices {
  constructor(
    private platformApiMethods: PlatformApiMethods,
    private documentServicesAPI: DocumentServicesObject,
    private experimentsAPI: IExperimentsApiAdapter,
  ) {}

  private intentInstallStore = new IntentInstallStore({
    options: {},
    appDefinitionIds: [],
  });

  private progressBarManager: ProgressBarManager | null = null;
  private panelsManager: PanelsManager | null = null;

  private applicationsInstaller = new ApplicationsInstaller();

  private shouldInstallPendingFlowAppDependencies(
    pendingFlowAppDefinitionIds: string[],
  ): boolean {
    return (
      pendingFlowAppDefinitionIds.length > 0 &&
      this.experimentsAPI.enabled(COMPANION_APP_DEPENDENCIES_INSTALL_SPEC)
    );
  }

  private getPendingFlowAppDependenciesInstallData(
    options: IntentInstallOptions,
    pendingFlowAppDefinitionIds: string[],
    scheduledAppDefinitionIds: string[],
  ): AppDependenciesInstallData {
    if (
      !this.shouldInstallPendingFlowAppDependencies(pendingFlowAppDefinitionIds)
    ) {
      return { appDefinitionIds: [], appsOptions: {} };
    }

    const installedAppDefinitionIds = this.documentServicesAPI.platform
      .getInstalledApps()
      .map((app) => app.appDefinitionId)
      .filter((appDefinitionId): appDefinitionId is string =>
        Boolean(appDefinitionId),
      );

    const installData = getMissingAppDependenciesInstallData(
      this.documentServicesAPI,
      installedAppDefinitionIds,
    );
    const ignoredAppDefinitionIds = new Set([
      ...(options.ignoredApps ?? []),
      ...scheduledAppDefinitionIds,
    ]);
    const appDefinitionIds = installData.appDefinitionIds.filter(
      (appDefinitionId) => !ignoredAppDefinitionIds.has(appDefinitionId),
    );
    const appsOptions = appDefinitionIds.reduce((acc, appDefinitionId) => {
      acc[appDefinitionId] = installData.appsOptions[appDefinitionId];
      return acc;
    }, {} as Record<string, AppInstallOption>);

    return { appDefinitionIds, appsOptions };
  }

  private async installReconciledAppDependencies(
    context: PlatformContext,
    installData: AppDependenciesInstallData,
    originalOptions: IntentInstallOptions,
  ): Promise<IntentInstallReturnValue> {
    if (!installData.appDefinitionIds.length) {
      return defaultIntentInstallReturn;
    }

    const options: IntentInstallOptions = {
      ...originalOptions,
      appsToInstall: installData.appDefinitionIds,
      appsOptions: {
        ...(originalOptions.appsOptions ?? {}),
        ...installData.appsOptions,
      },
      isSilent: true,
      disableProgressBar: true,
      shouldInstallPending: false,
    };
    const intentInstallStore = new IntentInstallStore({
      options,
      appDefinitionIds: installData.appDefinitionIds,
    });
    const progressBarManager = createProgressBarManager(
      this.platformApiMethods,
      intentInstallStore,
      options,
    );
    const applicationsInstaller = new ApplicationsInstaller();

    applicationsInstaller.setInstallationStrategy(
      new SerialInstallationStrategyV2(
        this.platformApiMethods,
        this.documentServicesAPI,
        this.experimentsAPI,
        intentInstallStore,
        progressBarManager,
      ),
    );

    await applicationsInstaller.install(
      context,
      installData.appDefinitionIds,
      options,
    );

    return {
      failedApps: intentInstallStore.getAppsWithErrors(),
      installedApps: intentInstallStore.getInstallationResults(),
    };
  }

  public async intentInstall(
    context: PlatformContext,
    originalOptions: IntentInstallOptions = {},
  ): Promise<IntentInstallReturnValue> {
    const host = getEditorInfo()?.host;

    const isImpersonated = getIsImpersonated();

    if (
      host === 'BLOCKS' ||
      (isImpersonated && !shouldForceAppInstallation())
    ) {
      return defaultIntentInstallReturn;
    }

    if (this.intentInstallStore.isRunning()) {
      if (isAppInitiator(originalOptions)) {
        if (!originalOptions.appsToInstall?.length) {
          return defaultIntentInstallReturn;
        }
        const nestedAppsToInstall = originalOptions.appsToInstall;
        try {
          await this.applicationsInstaller.install(
            context,
            nestedAppsToInstall,
            originalOptions,
          );

          const installationResults =
            this.intentInstallStore.getInstallationResults();
          const installedApps = nestedAppsToInstall.reduce((acc, appDefId) => {
            if (installationResults[appDefId]) {
              acc[appDefId] = installationResults[appDefId];
            }
            return acc;
          }, {} as Record<string, AddAppData>);

          return {
            failedApps: this.intentInstallStore.getAppsWithErrors(),
            installedApps,
          };
        } catch (e) {
          throw createIntentInstallError(
            IntentInstallErrorCode.NestedIntentInstallFailed,
          )
            .withParentError(e as Error)
            .withRunningInstallation({
              installationId: this.intentInstallStore.getInstallationId(),
              apps: this.intentInstallStore.getAppsToInstall(),
              origin: this.intentInstallStore.getOriginType(),
              isSilent: this.intentInstallStore.isSilent(),
            })
            .withApplicationsToInstall(nestedAppsToInstall)
            .withIsSilent(originalOptions.isSilent);
        }
      } else {
        throw createIntentInstallError(
          IntentInstallErrorCode.IntentInstallIsAlreadyRunning,
        )
          .withRunningInstallation({
            installationId: this.intentInstallStore.getInstallationId(),
            apps: this.intentInstallStore.getAppsToInstall(),
            origin: this.intentInstallStore.getOriginType(),
            isSilent: this.intentInstallStore.isSilent(),
          })
          .withRejectedInstallation({
            apps: originalOptions.appsToInstall,
            origin: originalOptions.installOrigin?.info?.type,
            initiator: originalOptions.installOrigin?.initiator,
          });
      }
    }

    // for easier E2E testing. can return data only in debug mode
    const intentInstallTestingParams = getIntentInstallTestParams();

    const options: IntentInstallOptions = {
      ...originalOptions,
      isSilent: intentInstallTestingParams.isSilent || originalOptions.isSilent,
    };

    const appsToInstall = options.appsToInstall ?? [];
    const ignoredApps = options.ignoredApps ?? [];

    const intentApp = getIntentAppFromQueryParams(ignoredApps);

    const shouldInstallPending = options.shouldInstallPending ?? true;

    const canInstallAppPredicate = canInstallApp(options);

    const allPendingApps = shouldInstallPending
      ? getPendingApps(this.documentServicesAPI)
      : [];

    const pendingApps = allPendingApps.filter(canInstallAppPredicate);
    const pendingFlowAppDefs = allPendingApps
      .map((app) => app.appDefinitionId)
      .concat(intentInstallTestingParams.pendingApps);

    const intentAppFromPending = allPendingApps.find(
      (app) => app.appDefinitionId === intentApp?.appDefId,
    );

    const pendingAppDefs = pendingApps
      .map((app) => app.appDefinitionId)
      .concat(intentInstallTestingParams.pendingApps);

    const ignoredAppsSet = new Set(ignoredApps);

    const getAppsToInstallWithIntent = (apps: string[]): string[] => {
      if (!intentApp?.appDefId) {
        return apps;
      }

      if (
        intentAppFromPending &&
        !canInstallAppPredicate(intentAppFromPending)
      ) {
        return apps;
      }

      return [...apps, intentApp.appDefId];
    };

    const baseAppsToInstall = uniq(
      [...appsToInstall, ...pendingAppDefs].filter((appDefId) => {
        const isIntentApp = appDefId === intentApp?.appDefId;
        const isIgnoredApp = ignoredAppsSet.has(appDefId);

        return !isIntentApp && !isIgnoredApp;
      }),
    );

    const allAppsToInstall = getAppsToInstallWithIntent(baseAppsToInstall);

    const appDefIdsToInstall = allAppsToInstall.filter((appDefId) => {
      const appData = this.documentServicesAPI.platform.getAppDataByAppDefId(
        appDefId,
      ) as EditorClientSpecMapEntry | undefined;

      if (options.allowReAddActiveApps) {
        return !appData || getAppType(appData) !== 'tpaSection';
      }

      return (
        !this.documentServicesAPI.platform.isAppActive(appDefId) ||
        (appData && isTpaWidgetApp(appData))
      );
    });
    const shouldInstallPendingFlowAppDependencies =
      this.shouldInstallPendingFlowAppDependencies(pendingFlowAppDefs);

    this.intentInstallStore = new IntentInstallStore({
      options,
      appDefinitionIds: appDefIdsToInstall,
      intentApp,
    });
    this.progressBarManager = createProgressBarManager(
      this.platformApiMethods,
      this.intentInstallStore,
      options,
    );
    const biManager = new BIManager(this.intentInstallStore);

    biManager.reportIntentInstallBegin(IntentInstallBIEventStep.CHECK_INSTALL);

    if (
      !appDefIdsToInstall.length &&
      !shouldInstallPendingFlowAppDependencies
    ) {
      return defaultIntentInstallReturn;
    }

    this.intentInstallStore.setRunning(true);

    biManager.reportIntentInstallBegin(IntentInstallBIEventStep.BEGIN_INSTALL);

    // based on the options we can decide which installation strategy to use

    this.applicationsInstaller.setInstallationStrategy(
      new SerialInstallationStrategyV2(
        this.platformApiMethods,
        this.documentServicesAPI,
        this.experimentsAPI,
        this.intentInstallStore,
        this.progressBarManager,
      ),
    );

    this.panelsManager = new PanelsManager(
      context,
      this.intentInstallStore,
      this.progressBarManager,
    );

    const appDefIds = this.intentInstallStore.getAppsToInstall();

    if (appDefIds.length) {
      this.progressBarManager.openInitialProgressBar();
    }

    try {
      if (appDefIds.length) {
        const appMarketListings =
          await appMarketService.getAppsInfoByLanguageWithDefaults({
            appDefinitionIds: appDefIds,
            languageCode: this.documentServicesAPI.generalInfo.getLanguage(),
          });
        this.intentInstallStore.setAppMarketListings(appMarketListings);

        if (
          this.experimentsAPI.enabled(PRE_INTENT_INSTALL_MODAL_EXPERIMENT) &&
          !options.isSilent
        ) {
          await this.panelsManager.openPreIntentInstallModal();
        }

        this.progressBarManager.startFakeProgressBarForCurrentApp();

        await this.applicationsInstaller.install(context, appDefIds, options);
      }

      try {
        const appDependenciesInstallData =
          this.getPendingFlowAppDependenciesInstallData(
            options,
            pendingFlowAppDefs,
            appDefIdsToInstall,
          );
        const reconciledDependenciesInstallResult =
          await this.installReconciledAppDependencies(
            context,
            appDependenciesInstallData,
            options,
          );

        if (reconciledDependenciesInstallResult.failedApps.length) {
          console.error(
            'Failed to install companion app dependencies:',
            reconciledDependenciesInstallResult.failedApps,
          );
        }
      } catch (error) {
        console.error('Failed to install companion app dependencies:', error);
      }
    } catch (e: unknown) {
      throw createIntentInstallError(IntentInstallErrorCode.IntentInstallFailed)
        .withParentError(e as Error)
        .withApplicationsToInstall(appDefIdsToInstall)
        .withIntentApp(intentApp?.appDefId)
        .withIsSilent(options.isSilent);
    } finally {
      await this.documentServicesAPI.waitForChangesAppliedAsync().catch((e) => {
        console.error(
          createIntentInstallError(
            IntentInstallErrorCode.WaitForChangesAppliedFailed,
          )
            .withParentError(e as Error)
            .withApplicationsToInstall(appDefIdsToInstall)
            .withIntentApp(intentApp?.appDefId)
            .withIsSilent(options.isSilent),
        );
      });

      this.intentInstallStore.setRunning(false);

      if (appDefIds.length && !options.keepProgressBarUntilResolved) {
        this.progressBarManager.closeProgressBar();
      }
      this.platformApiMethods.editor.updatePendingAppsNotification?.();
      if (appDefIds.length) {
        this.panelsManager.openBusinessPanel();
        this.panelsManager.openPlatformFailedAlert();
      }
    }
    if (!this.documentServicesAPI.transactions.isRunningAny()) {
      await this.platformApiMethods.editor.saveInBackground?.();
    }
    if (appDefIds.length && options.keepProgressBarUntilResolved) {
      this.progressBarManager.closeProgressBar();
    }
    return {
      failedApps: this.intentInstallStore.getAppsWithErrors(),
      installedApps: this.intentInstallStore.getInstallationResults(),
    };
  }

  public isIntentInstallRunning() {
    return this.intentInstallStore.isRunning();
  }

  public isIntentInstallRunningInSilentMode() {
    return this.isIntentInstallRunning() && this.intentInstallStore.isSilent();
  }

  public shouldUseWidgetAppsConfiguration() {
    if (!this.experimentsAPI.enabled(WIDGET_APPS_CONFIG_EXPERIMENT)) {
      return false;
    }

    return (
      this.intentInstallStore.isRunning() && !this.intentInstallStore.isSilent()
    );
  }

  public async setupIntentInstallWidgetsConfigFromUnifiedCollection(
    collection: UnifiedComponentsCollection,
    appName: string,
  ): Promise<void> {
    if (!this.shouldUseWidgetAppsConfiguration()) {
      return;
    }

    const widgets = Object.values(collection.widgets);
    const pages = Object.values(collection.pages);

    const filteredWidgets = widgets.filter(
      (widget) => widget.installation?.base?.essential,
    );

    this.intentInstallStore.setWidgetsConfiguration(
      (await this.panelsManager?.openWidgetAppsIntentInstallModal(
        filteredWidgets,
        pages,
        appName,
      )) ?? {},
    );
  }

  public getWidgetsConfiguration() {
    return this.intentInstallStore.getWidgetsConfiguration();
  }

  public getBIExtras(options?: GetBiExtrasOptions): {
    appType?: string;
    installationId?: string;
    origin?: string;
  } {
    if (!this.intentInstallStore.isRunning()) {
      return {};
    }

    const appType = (() => {
      if (!options?.appDefinitionId) {
        return;
      }

      return this.intentInstallStore.getIntentApp()?.appDefId ===
        options.appDefinitionId
        ? 'INTENT'
        : 'DEFAULT';
    })();

    return {
      installationId: this.intentInstallStore.getInstallationId(),
      origin: options?.origin || this.intentInstallStore.getOriginType(),
      ...(appType ? { appType } : {}),
    };
  }
}
